import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import { Observable, ReplaySubject} from 'rxjs';
import {first, take} from 'rxjs/operators';
import {PartnerFacility} from '../interface/partner-facility';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {ListResponse} from '../interface/response/list-response';
import {Ofwca} from '../interface/ofwca';
import {Uuid} from '../interface/response/uuid';
import {PartnerFacilityVerificationSalesProcess} from "../interface/partner-facility-verification-sales-process";

@Injectable({
  providedIn: 'root'
})
export class FacilityService {
  private readonly facilitiesListSubject: ReplaySubject<CountedAndPaginated<PartnerFacility>>
    = new ReplaySubject<CountedAndPaginated<PartnerFacility>>(1);
  private readonly facilitiesVerificationSalesProcessListSubject: ReplaySubject<CountedAndPaginated<PartnerFacilityVerificationSalesProcess>>
    = new ReplaySubject<CountedAndPaginated<PartnerFacilityVerificationSalesProcess>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public sendForm(formData: object, addressId?: string): Observable<any> {
    if (!addressId) {
      return this.httpClient.post(`/rest/crm/facility`, formData)
        .pipe(take(1));
    } else {
      return this.httpClient.put(`/rest/crm/facility/${addressId}`, formData)
        .pipe(take(1));
    }
  }

  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/facility?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<PartnerFacility>) => {
        this.facilitiesListSubject.next(result);
      });
  }

  public getFacilitiesListSubject(): ReplaySubject<CountedAndPaginated<PartnerFacility>> {
    return this.facilitiesListSubject;
  }
  public getFacilitiesVerificationSalesProcessListSubject(): ReplaySubject<CountedAndPaginated<PartnerFacilityVerificationSalesProcess>> {
    return this.facilitiesVerificationSalesProcessListSubject;
  }

  public downloadReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/facility/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadDobryAgentReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/dobry-agent/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadVerificationReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/facility/verificationSalesProcesss/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public get(addressId: string): Observable<any> {
    return this.httpClient.get(`/rest/crm/facility/${addressId}`)
      .pipe(take(1));
  }

  public getFacilityOfwcas(addressId: string): Observable<ListResponse<Ofwca>> {
    return this.httpClient.get(`/rest/crm/facility/${addressId}/ofwca`)
      .pipe(take(1)) as Observable<ListResponse<Ofwca>>;
  }

  public deleteFacility(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/facility/${uuid}`)
      .pipe(first());
  }

  public getFacilityVerificationSalesProcesss(addressId: string): Observable<ListResponse<Ofwca>> {
    return this.httpClient.get(`/rest/crm/facility/${addressId}/verificationSalesProcesss`)
      .pipe(take(1)) as Observable<ListResponse<Ofwca>>;
  }

  public getFacilityVerificationSalesProcess(verificationSalesId: string): Observable<PartnerFacilityVerificationSalesProcess> {
    return this.httpClient.get(`/rest/crm/facility/${verificationSalesId}/verificationSalesProcess`)
      .pipe(first()) as Observable<PartnerFacilityVerificationSalesProcess>;
  }

  public addFacilityVerificationSalesProcesss(addressId: string, formData: object ): Observable<any>  {
    return this.httpClient.post(`/rest/crm/facility/${addressId}/verificationSalesProcesss`, formData)
      .pipe(first());
  }

  public loadVerificationSalesProcessList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/verificationSalesProcesss?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<PartnerFacilityVerificationSalesProcess>) => {
        this.facilitiesVerificationSalesProcessListSubject.next(result);
      });
  }

  public downloadVeridicationSales(verificationSalesId: string): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/facility/${verificationSalesId}/verificationSalesProcesss/pdf`, {})
      .pipe(first()) as Observable<Uuid>;
  }

  public deleteVeridicationSales(verificationSalesId: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/facility/${verificationSalesId}/verificationSalesProcesss`)
      .pipe(first());
  }

  public downloadDocument(addressId: string, documentId: string): void {
    this.document.location.href = `/rest/crm/facility/${addressId}/downloadFile/${documentId}`;
  }
}
